import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, Globe, MapPin, Linkedin, MessageCircle, ArrowUpRight } from "lucide-react";
import PageShell from "@/components/opsiq/PageShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP_NUMBER = "233546080065";
const WHATSAPP_PREFILL = encodeURIComponent(
  "Hello OpsIQ — I'd like to talk about Operational Intelligence for my business."
);
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_PREFILL}`;

const CHANNELS = [
  { Icon: Mail, label: "Email", value: "info@OpsIQ.live", href: "mailto:info@OpsIQ.live" },
  { Icon: MessageCircle, label: "WhatsApp", value: "+233 546 080 065", href: WHATSAPP_URL, external: true, accent: true },
  { Icon: Phone, label: "Telephone", value: "+233 546 080 065", href: "tel:+233546080065" },
  { Icon: Globe, label: "Website", value: "www.OpsIQ.live", href: "https://www.opsiq.live" },
  { Icon: MapPin, label: "Office", value: "Accra, Ghana", href: "#" },
  { Icon: Linkedin, label: "LinkedIn", value: "OpsIQ", href: "#" },
];

const inputCls =
  "w-full bg-transparent border-b border-white/20 focus:border-[#D9FF3C] outline-none py-3 text-base text-[#F1ECE0] placeholder:text-[#F1ECE0]/35";

function Field({ label, children, testid }) {
  return (
    <label data-testid={testid} className="flex flex-col gap-2">
      <span className="text-[10px] font-plex-mono uppercase tracking-[0.22em] text-[#F1ECE0]/50">{label}</span>
      {children}
    </label>
  );
}

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", organization: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email.includes("@") || !form.message.trim()) {
      toast.error("Please fill in name, email and a message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent. We'll be in touch shortly.");
      setDone(true);
    } catch {
      toast.error("Something went wrong. Please try WhatsApp or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageShell
      testid="contact-page"
      eyebrow="Contact"
      title={<>Let&apos;s talk. <span className="italic">On your schedule.</span></>}
      subtitle="Send a message below, or reach us directly on WhatsApp, email or phone. For a working consultation, use the Book Consultation page — you'll get a confirmed 30-minute slot with an OpsIQ principal."
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 pb-12 md:pb-16">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {CHANNELS.map(({ Icon, label, value, href, external, accent }) => (
            <a
              key={label}
              href={href}
              {...(external ? { target: "_blank", rel: "noreferrer" } : {})}
              data-testid={`channel-${label.toLowerCase()}`}
              className={
                "group border p-6 md:p-7 flex flex-col gap-3 transition-colors " +
                (accent
                  ? "border-[#D9FF3C]/60 bg-[#D9FF3C]/10 hover:bg-[#D9FF3C] hover:text-[#142645]"
                  : "border-white/10 bg-[#142645]/50 hover:border-[#D9FF3C]")
              }
            >
              <div
                className={
                  "h-11 w-11 rounded-full flex items-center justify-center transition-colors " +
                  (accent ? "bg-[#D9FF3C] group-hover:bg-[#142645]" : "bg-[#D9FF3C]/12 group-hover:bg-[#D9FF3C]")
                }
              >
                <Icon
                  size={20}
                  strokeWidth={1.5}
                  className={
                    accent
                      ? "text-[#142645] group-hover:text-[#D9FF3C]"
                      : "text-[#D9FF3C] group-hover:text-[#142645]"
                  }
                />
              </div>
              <div className="text-[10px] font-plex-mono uppercase tracking-[0.22em] text-[#F1ECE0]/50 group-hover:text-inherit">{label}</div>
              <div className="text-lg text-[#F1ECE0] font-editorial leading-tight group-hover:text-inherit">{value}</div>
            </a>
          ))}
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 pb-20 md:pb-28">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-10">
          {/* Form */}
          <div className="lg:col-span-3">
            <div className="text-[10px] font-plex-mono uppercase tracking-[0.22em] text-[#D9FF3C] mb-4">
              Send a message
            </div>
            <h2 className="font-editorial text-3xl md:text-4xl leading-tight tracking-tight text-[#F1ECE0] mb-8">
              Tell us what you&apos;re trying to see, decide, or improve.
            </h2>

            {done ? (
              <div
                data-testid="contact-success"
                className="border border-[#D9FF3C]/40 bg-[#142645]/60 p-10"
              >
                <div className="text-[10px] font-plex-mono uppercase tracking-[0.22em] text-[#D9FF3C] mb-4">
                  Message received
                </div>
                <h3 className="font-editorial text-3xl leading-tight text-[#F1ECE0]">
                  Thank you, {form.name.split(" ")[0]}.
                </h3>
                <p className="mt-3 text-[#F1ECE0]/75">
                  We&apos;ll reply to <span className="text-[#F1ECE0]">{form.email}</span> shortly. For anything urgent, ping us on{" "}
                  <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" className="text-[#D9FF3C] hover:underline">WhatsApp</a>.
                </p>
              </div>
            ) : (
              <form
                onSubmit={submit}
                data-testid="contact-form"
                className="border border-white/10 bg-[#142645]/70 backdrop-blur-md p-6 md:p-10 flex flex-col gap-5"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field label="Full name" testid="cf-name">
                    <input
                      required
                      value={form.name}
                      onChange={update("name")}
                      data-testid="cf-name-input"
                      className={inputCls}
                      placeholder="Kwame Mensah"
                    />
                  </Field>
                  <Field label="Email" testid="cf-email">
                    <input
                      required
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      data-testid="cf-email-input"
                      className={inputCls}
                      placeholder="k.mensah@company.com"
                    />
                  </Field>
                  <Field label="Organisation" testid="cf-org">
                    <input
                      value={form.organization}
                      onChange={update("organization")}
                      data-testid="cf-org-input"
                      className={inputCls}
                      placeholder="Company Ltd (optional)"
                    />
                  </Field>
                  <Field label="Subject" testid="cf-subject">
                    <input
                      value={form.subject}
                      onChange={update("subject")}
                      data-testid="cf-subject-input"
                      className={inputCls}
                      placeholder="Dashboards / KPI review / other"
                    />
                  </Field>
                </div>
                <Field label="Message" testid="cf-message">
                  <textarea
                    required
                    value={form.message}
                    onChange={update("message")}
                    rows={5}
                    data-testid="cf-message-input"
                    className={inputCls}
                    placeholder="A short note about what you'd like to discuss."
                  />
                </Field>
                <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="cf-submit-btn"
                    className="inline-flex items-center justify-center gap-2 px-6 py-4 bg-[#D9FF3C] text-[#142645] text-xs uppercase tracking-[0.22em] font-semibold hover:bg-[#E4FF66] transition-colors disabled:opacity-60"
                  >
                    {loading ? "Sending..." : "Send message"} <ArrowUpRight size={16} />
                  </button>
                  <a
                    href={WHATSAPP_URL}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="cf-whatsapp-btn"
                    className="inline-flex items-center justify-center gap-2 px-6 py-4 border border-[#D9FF3C]/50 text-[#D9FF3C] text-xs uppercase tracking-[0.22em] font-semibold hover:bg-[#D9FF3C] hover:text-[#142645] transition-colors"
                  >
                    <MessageCircle size={16} /> Chat on WhatsApp
                  </a>
                </div>
                <p className="text-[10px] font-plex-mono uppercase tracking-[0.22em] text-[#F1ECE0]/45">
                  Confidential · No sales pitch · We reply within one business day
                </p>
              </form>
            )}
          </div>

          {/* Side panel */}
          <aside className="lg:col-span-2">
            <div className="border border-white/10 bg-[#142645]/50 p-6 md:p-8">
              <div className="text-[10px] font-plex-mono uppercase tracking-[0.22em] text-[#D9FF3C] mb-3">
                Prefer WhatsApp?
              </div>
              <h3 className="font-editorial text-2xl leading-tight text-[#F1ECE0] mb-4">
                Message us instantly on WhatsApp.
              </h3>
              <p className="text-sm text-[#F1ECE0]/70 leading-relaxed mb-6">
                The fastest way to reach us. We monitor WhatsApp during business hours and typically reply within the hour.
              </p>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="whatsapp-cta"
                className="inline-flex items-center gap-2 px-5 py-3 bg-[#25D366] text-white text-xs uppercase tracking-[0.22em] font-semibold hover:opacity-90 transition-opacity"
              >
                <MessageCircle size={16} /> Open WhatsApp
              </a>
            </div>

            <div className="border border-white/10 bg-[#142645]/50 p-6 md:p-8 mt-4">
              <div className="text-[10px] font-plex-mono uppercase tracking-[0.22em] text-[#D9FF3C] mb-3">
                Ready for a working session?
              </div>
              <h3 className="font-editorial text-2xl leading-tight text-[#F1ECE0] mb-4">
                Book a 30-minute consultation with a principal.
              </h3>
              <a
                href="/book"
                className="inline-flex items-center gap-2 px-5 py-3 border border-white/25 text-[#F1ECE0] text-xs uppercase tracking-[0.22em] font-semibold hover:border-[#D9FF3C] hover:text-[#D9FF3C] transition-colors"
              >
                Book consultation <ArrowUpRight size={14} />
              </a>
            </div>
          </aside>
        </div>
      </div>
    </PageShell>
  );
}
